import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { PLATFORM, STREAMS_PLATFORM_ENABLED } from '@/constants/platform'
import type { DGEAuthMethod, Platform } from '@/types/platform'

type MinimumUserType = {
  name: string
  email: string
}

export type AuthStore = {
  userRole: string | null
  token: string | null
  platform: Platform
  dgeAuthMethod?: DGEAuthMethod
  isDDE: boolean
  isStreaming: boolean
  user?: MinimumUserType
  isAuthenticated: boolean
  setAuthData: (
    token: string,
    userRole: string,
    platform: Platform,
    user: MinimumUserType,
    dgeAuthMethod?: DGEAuthMethod
  ) => void
  setPlatform: (platform: Platform) => void
  clearAuthData: () => void
}

const DEFAULT_STATE = {
  token: null,
  platform: PLATFORM.DDE,
  isDDE: true,
  isStreaming: false,
  userRole: null,
  isAuthenticated: false,
  user: undefined,
  dgeAuthMethod: undefined
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      ...DEFAULT_STATE,
      setAuthData: (token, userRole, platform, user, dgeAuthMethod) =>
        set({
          token,
          userRole,
          platform,
          isAuthenticated: true,
          user,
          dgeAuthMethod,
          isDDE: platform === PLATFORM.DDE,
          isStreaming: platform === PLATFORM.DGE
        }),
      setPlatform: (platform) => set({ platform }),
      clearAuthData: () => set(DEFAULT_STATE)
    }),
    {
      name: 'auth-storage',
      onRehydrateStorage: () => (state) => {
        // A session opened while Streams was still available would otherwise keep
        // rendering DGE views against a backend this build cannot reach.
        if (state && !STREAMS_PLATFORM_ENABLED && state.platform === PLATFORM.DGE) {
          Object.assign(state, DEFAULT_STATE)
        }
      }
    }
  )
)
